import { format, formatDistanceToNow, parseISO } from 'date-fns';

// ── Currency ──────────────────────────────────────────────────
export const formatCurrency = (amount: number, currency = 'GHS'): string => {
  return new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

export const formatNumber = (num: number): string =>
  new Intl.NumberFormat('en-GH').format(num || 0);

// ── Dates ─────────────────────────────────────────────────────
export const formatDate = (date: string | Date): string => {
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return format(d, 'dd MMM yyyy');
  } catch { return '-'; }
};

export const formatDateTime = (date: string | Date): string => {
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return format(d, 'dd MMM yyyy, HH:mm');
  } catch { return '-'; }
};

export const timeAgo = (date: string | Date): string => {
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return '-'; }
};

export const todayStr = (): string => format(new Date(), 'yyyy-MM-dd');
export const monthStartStr = (): string =>
  format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd');

// ── Role helpers ──────────────────────────────────────────────
export const roleLabel: Record<string, string> = {
  super_admin: 'Super Admin',
  farm_manager: 'Farm Manager',
  employee: 'Employee',
  sales_officer: 'Sales Officer',
  accountant: 'Accountant',
};

export const roleBadgeClass: Record<string, string> = {
  super_admin: 'badge-blue',
  farm_manager: 'badge-green',
  employee: 'badge-gray',
  sales_officer: 'badge-yellow',
  accountant: 'badge-blue',
};

// ── Status helpers ────────────────────────────────────────────
export const statusBadgeClass: Record<string, string> = {
  active: 'badge-green',
  inactive: 'badge-red',
  archived: 'badge-gray',
  completed: 'badge-green',
  pending: 'badge-yellow',
  cancelled: 'badge-red',
};

export const activityTypeLabel: Record<string, string> = {
  feed: 'Feed Given',
  water: 'Water Supply',
  mortality: 'Mortality',
  medication: 'Medication',
  vaccination: 'Vaccination',
  cleaning: 'Cleaning',
  sanitation: 'Sanitation',
  transfer: 'Transfer',
  environmental: 'Environmental Check',
  observation: 'Observation',
  incident: 'Incident / Issue',
  maintenance: 'Maintenance',
  other: 'Other',
};

export const activityTypeIcon: Record<string, string> = {
  feed: '🌾', water: '💧', mortality: '💀', medication: '💊',
  vaccination: '💉', cleaning: '🧹', sanitation: '🧴', transfer: '🔄',
  environmental: '🌡️', observation: '👁️', incident: '⚠️', maintenance: '🔧', other: '📝',
};

export const expenseCategoryLabel: Record<string, string> = {
  feed: 'Feed Purchase',
  drugs: 'Drugs / Medicine',
  vaccines: 'Vaccines',
  equipment: 'Equipment',
  salary: 'Salaries & Wages',
  transport: 'Transport',
  repairs: 'Repairs & Maintenance',
  utilities: 'Utilities',
  miscellaneous: 'Miscellaneous',
};

export const paymentMethodLabel: Record<string, string> = {
  cash: 'Cash',
  momo: 'Mobile Money',
  bank: 'Bank Transfer',
  credit: 'Credit',
};

// ── Misc ──────────────────────────────────────────────────────
export const getInitials = (firstName: string, lastName: string): string =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

export const truncate = (str: string, len = 40): string =>
  str?.length > len ? `${str.slice(0, len)}…` : str || '';

export const getProductTypeLabel: Record<string, string> = {
  eggs: 'Eggs',
  birds: 'Live Birds',
  feed: 'Feed',
  manure: 'Manure',
  other: 'Other',
};

export const clsx = (...classes: (string | undefined | null | false)[]): string =>
  classes.filter(Boolean).join(' ');
